import { storableError } from '../../util/errors';
import { fetchCurrentUser } from '../../ducks/user.duck';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { denormalisedResponseEntities } from '../../util/data';
import { availabilityExceptionsAPI, audienceAPI, analyticsAPI, assignmentsAPI } from '../../api';
import { computePerformanceMetrics } from '../../util/performanceMetrics';
import config from '../../config';

// ================ Action types ================ //

export const SET_TASK_COMPLETED = 'app/CreatorHomePage/SET_TASK_COMPLETED';
export const SET_WIZARD_STATE = 'app/CreatorHomePage/SET_WIZARD_STATE';

// Live rooms
export const FETCH_LIVE_ROOMS_REQUEST = 'app/CreatorHomePage/FETCH_LIVE_ROOMS_REQUEST';
export const FETCH_LIVE_ROOMS_SUCCESS = 'app/CreatorHomePage/FETCH_LIVE_ROOMS_SUCCESS';
export const FETCH_LIVE_ROOMS_ERROR = 'app/CreatorHomePage/FETCH_LIVE_ROOMS_ERROR';

// Episodes
export const FETCH_LATEST_EPISODE_REQUEST = 'app/CreatorHomePage/FETCH_LATEST_EPISODE_REQUEST';
export const FETCH_LATEST_EPISODE_SUCCESS = 'app/CreatorHomePage/FETCH_LATEST_EPISODE_SUCCESS';
export const FETCH_LATEST_EPISODE_ERROR = 'app/CreatorHomePage/FETCH_LATEST_EPISODE_ERROR';

// Performance metrics
// Ended assignments — powers the dismissible "Gig Boost ended" notice.
export const FETCH_ENDED_ASSIGNMENTS_SUCCESS =
  'app/CreatorHomePage/FETCH_ENDED_ASSIGNMENTS_SUCCESS';

export const FETCH_PERFORMANCE_REQUEST = 'app/CreatorHomePage/FETCH_PERFORMANCE_REQUEST';
export const FETCH_PERFORMANCE_SUCCESS = 'app/CreatorHomePage/FETCH_PERFORMANCE_SUCCESS';
export const FETCH_PERFORMANCE_ERROR = 'app/CreatorHomePage/FETCH_PERFORMANCE_ERROR';

// ================ Reducer ================ //

const initialState = {
  endedAssignments: [],
  tasks: {
    getFirst20MembersTask: {
      completed: false,
      inProgress: false,
      error: null,
    },
    completeLiveRoomTask: {
      completed: false,
      inProgress: false,
      error: null,
    },
    scheduleLiveRoomTask: {
      completed: false,
      inProgress: false,
      error: null,
    },
    turnOnPaidMembershipsTask: {
      completed: false,
      inProgress: false,
      error: null,
    },
    completeCreatorProfileTask: {
      completed: false,
      inProgress: false,
      error: null,
    },
  },
  wizardState: {
    dataLoaded: false,
    shouldShow: false,
    shouldAutoShow: false,
    startingStep: 1,
    hasIncompleteSteps: false,
  },
  // Live rooms for Latest Performance card
  liveRoomIds: [],
  liveRoomsInProgress: false,
  liveRoomsLoaded: false,
  liveRoomsError: null,
  // Latest completed episode
  latestEpisode: null,
  latestEpisodeInProgress: false,
  latestEpisodeError: null,
  // Performance metrics for overview card
  performanceMetrics: null,
  performanceInProgress: false,
  performanceError: null,
};

export default function creatorHomePageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case SET_TASK_COMPLETED:
      return {
        ...state,
        tasks: {
          ...state.tasks,
          [payload.taskName]: {
            ...state.tasks[payload.taskName],
            completed: payload.completed,
            inProgress: false,
            error: null,
          },
        },
      };

    case SET_WIZARD_STATE:
      return {
        ...state,
        wizardState: {
          ...state.wizardState,
          ...payload,
        },
      };

    // Live rooms
    case FETCH_LIVE_ROOMS_REQUEST:
      return {
        ...state,
        liveRoomsInProgress: true,
        liveRoomsError: null,
      };
    case FETCH_LIVE_ROOMS_SUCCESS:
      return {
        ...state,
        liveRoomIds: payload.liveRoomIds,
        liveRoomsInProgress: false,
        liveRoomsLoaded: true,
      };
    case FETCH_LIVE_ROOMS_ERROR:
      return {
        ...state,
        liveRoomsInProgress: false,
        liveRoomsError: payload,
      };

    // Latest episode
    case FETCH_LATEST_EPISODE_REQUEST:
      return {
        ...state,
        latestEpisodeInProgress: true,
        latestEpisodeError: null,
      };
    case FETCH_LATEST_EPISODE_SUCCESS:
      return {
        ...state,
        latestEpisode: payload.episode,
        latestEpisodeInProgress: false,
      };
    case FETCH_LATEST_EPISODE_ERROR:
      return {
        ...state,
        latestEpisodeInProgress: false,
        latestEpisodeError: payload,
      };

    // Performance metrics
    case FETCH_ENDED_ASSIGNMENTS_SUCCESS:
      return { ...state, endedAssignments: payload };

    case FETCH_PERFORMANCE_REQUEST:
      return {
        ...state,
        performanceInProgress: true,
        performanceError: null,
      };
    case FETCH_PERFORMANCE_SUCCESS:
      return {
        ...state,
        performanceMetrics: payload,
        performanceInProgress: false,
      };
    case FETCH_PERFORMANCE_ERROR:
      return {
        ...state,
        performanceInProgress: false,
        performanceError: payload,
      };

    default:
      return state;
  }
}

// ================ Action creators ================ //

export const setTaskCompleted = (taskName, isCompleted) => ({
  type: SET_TASK_COMPLETED,
  payload: {
    taskName,
    completed: isCompleted,
  },
});

export const setWizardState = wizardState => ({
  type: SET_WIZARD_STATE,
  payload: wizardState,
});

// Live rooms action creators
export const fetchLiveRoomsRequest = () => ({
  type: FETCH_LIVE_ROOMS_REQUEST,
});

export const fetchLiveRoomsSuccess = liveRoomIds => ({
  type: FETCH_LIVE_ROOMS_SUCCESS,
  payload: { liveRoomIds },
});

export const fetchLiveRoomsError = error => ({
  type: FETCH_LIVE_ROOMS_ERROR,
  payload: error,
});

// Latest episode action creators
export const fetchLatestEpisodeRequest = () => ({
  type: FETCH_LATEST_EPISODE_REQUEST,
});

export const fetchLatestEpisodeSuccess = episode => ({
  type: FETCH_LATEST_EPISODE_SUCCESS,
  payload: { episode },
});

export const fetchLatestEpisodeError = error => ({
  type: FETCH_LATEST_EPISODE_ERROR,
  payload: error,
});

// Performance metrics action creators
export const fetchPerformanceRequest = () => ({
  type: FETCH_PERFORMANCE_REQUEST,
});

export const fetchPerformanceSuccess = data => ({
  type: FETCH_PERFORMANCE_SUCCESS,
  payload: data,
});

export const fetchPerformanceError = error => ({
  type: FETCH_PERFORMANCE_ERROR,
  payload: error,
});

// ================ Selectors ================ //

export const selectWizardState = state => state.CreatorHomePage.wizardState;
export const selectTasks = state => state.CreatorHomePage.tasks;
export const selectLiveRoomIds = state => state.CreatorHomePage.liveRoomIds;
export const selectLiveRoomsInProgress = state => state.CreatorHomePage.liveRoomsInProgress;
export const selectLiveRoomsLoaded = state => state.CreatorHomePage.liveRoomsLoaded;
export const selectLatestEpisode = state => state.CreatorHomePage.latestEpisode;
export const selectLatestEpisodeInProgress = state => state.CreatorHomePage.latestEpisodeInProgress;
export const selectPerformanceMetrics = state => state.CreatorHomePage.performanceMetrics;
export const selectPerformanceInProgress = state => state.CreatorHomePage.performanceInProgress;

// ================ Thunks ================ //

// Check if creator profile is complete based on publicData.creatorProfile
const isCreatorProfileComplete = currentUser => {
  const creatorProfile = currentUser?.attributes?.profile?.publicData?.creatorProfile;
  if (!creatorProfile) return false;

  // Profile is complete if isProfileComplete flag is set
  // OR if they have filled essential fields (artistAlias + bio)
  if (creatorProfile.isProfileComplete) return true;

  const hasArtistAlias = creatorProfile.artistAlias && creatorProfile.artistAlias.trim() !== '';
  const hasBio = creatorProfile.bio && creatorProfile.bio.trim() !== '';

  return hasArtistAlias && hasBio;
};

export const fetchCreatorProfileCompleted = () => async (dispatch, getState, sdk) => {
  try {
    // Fetch fresh current user data to ensure we have the latest profile
    await dispatch(fetchCurrentUser());

    const state = getState();
    const currentUser = state.user.currentUser;
    const isCompleted = isCreatorProfileComplete(currentUser);

    dispatch(setTaskCompleted('completeCreatorProfileTask', isCompleted));
    return isCompleted;
  } catch (e) {
    console.error('Error fetching creator profile status:', e);
    return false;
  }
};

export const fetchLiveRoomCompleted = () => async (dispatch, getState, sdk) => {
  try {
    // Query user's own live-room listings (includes drafts)
    const listingsResponse = await sdk.ownListings.query({
      pub_type: config.listingTypes['live-room'],
    });
    const listings = listingsResponse.data.data || [];

    // "Complete your Live Room" — at least one published live room
    const hasPublishedLiveRoom = listings.some(l => l.attributes.state === 'published');
    dispatch(setTaskCompleted('completeLiveRoomTask', hasPublishedLiveRoom));

    // "Schedule your Live Room Episodes" — at least one episode in any live room
    let hasEpisodes = false;
    for (const listing of listings) {
      try {
        const episodesResponse = await availabilityExceptionsAPI.query({
          listingId: listing.id.uuid,
        });
        const episodes = episodesResponse?.data?.data?.data || [];
        if (episodes.length > 0) {
          hasEpisodes = true;
          break;
        }
      } catch (e) {
        // Continue checking other listings
      }
    }
    dispatch(setTaskCompleted('scheduleLiveRoomTask', hasEpisodes));

    return hasPublishedLiveRoom;
  } catch (e) {
    console.error('Error fetching live room status:', e);
    return false;
  }
};

// Fetch published live rooms for Latest Performance card
export const fetchPublishedLiveRooms = () => async (dispatch, getState, sdk) => {
  dispatch(fetchLiveRoomsRequest());

  try {
    const response = await sdk.ownListings.query({
      pub_type: config.listingTypes['live-room'],
      states: ['published'],
      include: ['images'],
      'fields.image': ['variants.square-small', 'variants.square-small2x'],
      'limit.images': 1,
    });

    const listings = response.data.data || [];
    const liveRoomIds = listings.map(l => l.id);

    dispatch(addMarketplaceEntities(response));
    dispatch(fetchLiveRoomsSuccess(liveRoomIds));

    return listings;
  } catch (e) {
    console.error('Error fetching published live rooms:', e);
    dispatch(fetchLiveRoomsError(storableError(e)));
    return [];
  }
};

// Fetch the latest completed episode for Latest Episode card
export const fetchLatestCompletedEpisode = () => async (dispatch, getState, sdk) => {
  dispatch(fetchLatestEpisodeRequest());

  try {
    // First, get all published live rooms to find episodes
    const listingsResponse = await sdk.ownListings.query({
      pub_type: config.listingTypes['live-room'],
      states: ['published'],
    });

    const listings = listingsResponse.data.data || [];

    if (listings.length === 0) {
      dispatch(fetchLatestEpisodeSuccess(null));
      return null;
    }

    // Fetch episodes for all live rooms
    const now = new Date();
    let allCompletedEpisodes = [];

    for (const listing of listings) {
      try {
        const episodesResponse = await availabilityExceptionsAPI.query({
          listingId: listing.id.uuid,
        });

        // Our API sends the SDK's whole answer, and axios puts that under its own `data`
        const episodes = episodesResponse?.data?.data?.data || [];

        // Filter to only completed episodes (end date is in the past)
        const completedEpisodes = episodes.filter(episode => {
          const endDate = episode.attributes?.end ? new Date(episode.attributes.end) : null;
          return endDate && endDate < now;
        });

        // Add listing info to each episode
        completedEpisodes.forEach(episode => {
          allCompletedEpisodes.push({
            ...episode,
            listing: listing,
          });
        });
      } catch (e) {
        console.error(`Error fetching episodes for listing ${listing.id.uuid}:`, e);
      }
    }

    // Sort by end date descending to get the most recent completed episode
    allCompletedEpisodes.sort((a, b) => {
      const dateA = new Date(a.attributes?.end || 0);
      const dateB = new Date(b.attributes?.end || 0);
      return dateB - dateA;
    });

    const latestEpisode = allCompletedEpisodes.length > 0 ? allCompletedEpisodes[0] : null;
    dispatch(fetchLatestEpisodeSuccess(latestEpisode));

    return latestEpisode;
  } catch (e) {
    console.error('Error fetching latest completed episode:', e);
    dispatch(fetchLatestEpisodeError(storableError(e)));
    return null;
  }
};

/**
 * Fetch real performance metrics: subscribers (audience), revenue (transactions), views (GA4).
 * Computes daily time-series + totals + percent change vs previous period.
 */
export const fetchPerformanceMetrics = (rangeDays = 190) => async (dispatch, getState, sdk) => {
  dispatch(fetchPerformanceRequest());

  try {
    // Fetch all three data sources in parallel
    const [transactions, audienceLists, pageViewData] = await Promise.all([
      // Transactions (own sales)
      sdk.transactions.query({ only: 'sale', include: ['listing'] }).then(res => {
        dispatch(addMarketplaceEntities(res));
        return denormalisedResponseEntities(res);
      }),
      // Audience lists for subscriber count
      audienceAPI
        .getLists()
        .then(res => res.data?.data?.lists || [])
        .catch(() => []),
      // GA4 page views
      analyticsAPI
        .getPageViews({
          startDate: `${rangeDays}daysAgo`,
          endDate: 'today',
        })
        .then(res => res.data?.data || { totalViews: 0, previousTotalViews: 0, viewsByDate: {} })
        .catch(() => ({
          totalViews: 0,
          previousTotalViews: 0,
          viewsByDate: {},
        })),
    ]);

    const metrics = {
      ...computePerformanceMetrics({ transactions, audienceLists, pageViewData, rangeDays }),
      // Keep raw data for re-processing on range change
      _rawTransactions: transactions,
      _rawAudienceLists: audienceLists,
      _rawPageViewData: pageViewData,
    };

    dispatch(fetchPerformanceSuccess(metrics));
    return metrics;
  } catch (e) {
    console.error('Error fetching performance metrics:', e);
    dispatch(fetchPerformanceError(storableError(e)));
    return null;
  }
};

/**
 * Re-process performance metrics when the date range changes. Revenue +
 * subscribers re-slice from the cached transactions/audience instantly;
 * page views are a GA4 per-window aggregate, so we re-fetch them for the
 * new range and re-process so Total Views / Conversion aren't frozen at
 * the previous window. A race guard prevents a stale response from
 * overwriting a newer selection.
 */
export const changePerformanceDateRange = rangeDays => async (dispatch, getState) => {
  const prev = getState().CreatorHomePage.performanceMetrics;
  if (!prev || !prev._rawTransactions) return;

  const reprocess = pageViewData => {
    const cur = getState().CreatorHomePage.performanceMetrics || prev;
    const transactions = cur._rawTransactions || [];
    const audienceLists = cur._rawAudienceLists || [];
    dispatch(
      fetchPerformanceSuccess({
        ...computePerformanceMetrics({ transactions, audienceLists, pageViewData, rangeDays }),
        _rawTransactions: transactions,
        _rawAudienceLists: audienceLists,
        _rawPageViewData: pageViewData,
        _rangeDays: rangeDays,
      })
    );
  };

  // 1) Instant pass with the cached page views (revenue/subscribers/chart
  //    update immediately for the new window).
  reprocess(prev._rawPageViewData);

  // 2) Re-fetch the window-aggregated page views and re-process.
  try {
    const res = await analyticsAPI.getPageViews({
      startDate: `${rangeDays}daysAgo`,
      endDate: 'today',
    });
    const pageViewData = res.data?.data || {
      totalViews: 0,
      previousTotalViews: 0,
      viewsByDate: {},
    };
    if (getState().CreatorHomePage.performanceMetrics?._rangeDays === rangeDays) {
      reprocess(pageViewData);
    }
  } catch (e) {
    console.warn('Page views refetch on range change failed:', e?.message || e);
  }
};

// Helper to determine the starting step for the wizard
const calculateStartingStep = (creatorProfileCompleted, liveRoomCompleted) => {
  if (!creatorProfileCompleted) return 1;
  if (!liveRoomCompleted) return 2;
  return null; // All complete - wizard shouldn't show
};

// Was the onboarding wizard already completed for this user? Server
// truth (privateData.creatorOnboardingCompleted) takes precedence over
// localStorage so the flag survives logout, fresh browsers, and new
// devices — fixing Yuko's "pop-up re-appears every time" report which
// was caused by relying on localStorage alone.
const wasWizardShownForUser = (userId, currentUser) => {
  const fromProfile = currentUser?.attributes?.profile?.privateData?.creatorOnboardingCompleted;
  if (fromProfile === true) return true;
  if (typeof window === 'undefined') return false;
  try {
    return localStorage.getItem(`creator_wizard_shown_${userId}`) === 'true';
  } catch (e) {
    return false;
  }
};

// Mark the onboarding wizard as completed. Writes to localStorage for
// instant same-session quiet AND to Sharetribe privateData for durable
// cross-device persistence. Thunk shape so the page can dispatch it
// from any handler (close X, skip, completion).
export const markWizardAsShownForUser = () => async (dispatch, getState, sdk) => {
  const state = getState();
  const currentUser = state.user?.currentUser;
  const userId = currentUser?.id?.uuid;
  if (!userId) return;

  if (typeof window !== 'undefined') {
    try {
      localStorage.setItem(`creator_wizard_shown_${userId}`, 'true');
    } catch (e) {
      // Ignore localStorage errors
    }
  }

  try {
    await sdk.currentUser.updateProfile(
      { privateData: { creatorOnboardingCompleted: true } },
      { expand: true }
    );
  } catch (e) {
    // Non-fatal — localStorage still covers this session. Logged so we
    // can spot a Sharetribe outage if it ever recurs at scale.
    // eslint-disable-next-line no-console
    console.error('Failed to persist creatorOnboardingCompleted to privateData:', e);
  }
};

/**
 * Assignments that have ended, for the dashboard notice. Deliberately
 * best-effort: the home page must still render if this call fails, so the
 * error is swallowed and the notice simply doesn't appear.
 */
export const fetchEndedAssignments = () => async dispatch => {
  try {
    const res = await assignmentsAPI.listForCreator();
    const body = res?.data ?? res ?? {};
    const list = (body?.data ?? body)?.assignments || [];
    dispatch({
      type: FETCH_ENDED_ASSIGNMENTS_SUCCESS,
      payload: list.filter(a => ['revoked', 'expired'].includes(a?.status)),
    });
  } catch (e) {
    // Non-fatal — no notice is better than a broken dashboard.
  }
};

export const loadData = (params, search) => async (dispatch, getState, sdk) => {
  // Fetch all task completion statuses and dashboard data in parallel
  const [creatorProfileCompleted, liveRoomCompleted] = await Promise.all([
    dispatch(fetchCreatorProfileCompleted()),
    dispatch(fetchLiveRoomCompleted()),
    // Also fetch data for dashboard cards
    dispatch(fetchPublishedLiveRooms()),
    dispatch(fetchLatestCompletedEpisode()),
    dispatch(fetchPerformanceMetrics()),
    dispatch(fetchEndedAssignments()),
  ]);

  // For now, mark these tasks as incomplete (they require manual completion or additional API calls)
  // getFirst20MembersTask - would check member/fan count
  // turnOnPaidMembershipsTask - would check if paid memberships are enabled
  dispatch(setTaskCompleted('getFirst20MembersTask', false));
  dispatch(setTaskCompleted('turnOnPaidMembershipsTask', false));

  // Get current user for localStorage check
  const state = getState();
  const currentUser = state.user.currentUser;
  const userId = currentUser?.id?.uuid;

  // Calculate wizard state based on completed tasks
  const startingStep = calculateStartingStep(creatorProfileCompleted, liveRoomCompleted);
  const shouldShow = startingStep !== null;

  // Check if wizard was already completed (privateData + localStorage)
  const wasAlreadyShown = wasWizardShownForUser(userId, currentUser);
  const shouldAutoShow = shouldShow && !wasAlreadyShown;

  // Dispatch wizard state
  dispatch(
    setWizardState({
      dataLoaded: true,
      shouldShow,
      shouldAutoShow,
      startingStep: startingStep || 1,
      hasIncompleteSteps: shouldShow,
    })
  );

  return { creatorProfileCompleted, liveRoomCompleted };
};

// Update creator profile using SDK
export const updateCreatorProfile = profileData => async (dispatch, getState, sdk) => {
  try {
    await sdk.currentUser.updateProfile({
      publicData: {
        creatorProfile: profileData,
      },
      privateData: {
        onboardingCompleted: {
          creator: true,
        },
      },
    });

    // Refresh current user to get updated profile
    await dispatch(fetchCurrentUser());

    return { success: true };
  } catch (e) {
    console.error('Error updating creator profile:', e);
    throw e;
  }
};

// Thunk to recalculate wizard state after a step is completed
export const recalculateWizardState = () => (dispatch, getState) => {
  const state = getState();
  const { tasks } = state.CreatorHomePage;

  const creatorProfileCompleted = tasks.completeCreatorProfileTask.completed;
  const liveRoomCompleted = tasks.scheduleLiveRoomTask.completed;

  // Calculate starting step - returns null when all complete
  const startingStep = calculateStartingStep(creatorProfileCompleted, liveRoomCompleted);
  const shouldShow = startingStep !== null;

  // Dispatch updated wizard state (keep shouldAutoShow as false since this is a recalculation)
  dispatch(
    setWizardState({
      dataLoaded: true,
      shouldShow,
      shouldAutoShow: false,
      startingStep: startingStep || 1,
      hasIncompleteSteps: shouldShow,
    })
  );
};
